import { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ChevronDown, Menu, X, Bot, Inbox, Code2, MailOpen, BookOpen, Workflow, BarChart3, ShieldCheck, ShoppingBag, Layers, Store, Briefcase, Terminal, FileText, Webhook, History, Activity, Plug, Lock, Building2, Mail, LayoutGrid } from "lucide-react";
import { Logo } from "../Logo";

export const MENUS = {
  Product: [
    ["/product", "Platform overview", "How the pieces fit together", LayoutGrid],
    ["/product/ai-agents", "AI agents", "Grounded answers with approval", Bot],
    ["/product/shared-inbox", "Shared inbox", "Assign, collaborate, resolve", Inbox],
    ["/product/email-api", "Email API", "Transactional sending", Code2],
    ["/product/inbound-email", "Inbound email", "Receive and route mail", MailOpen],
    ["/product/knowledge-base", "Knowledge base", "Crawl, index, cite", BookOpen],
    ["/product/automations", "Automations", "Visual support workflows", Workflow],
    ["/product/analytics", "Analytics", "Volume, quality, cost", BarChart3],
    ["/product/email-deliverability", "Deliverability", "SPF, DKIM, suppressions", ShieldCheck],
  ],
  Solutions: [
    ["/solutions/ecommerce", "E-commerce", "Orders, shipping, returns", ShoppingBag],
    ["/solutions/saas", "SaaS", "Billing, onboarding, product help", Layers],
    ["/solutions/small-business", "Small business", "A support team in a box", Store],
    ["/solutions/service-businesses", "Service businesses", "Bookings and client questions", Briefcase],
    ["/solutions/developers", "Developers", "Email infrastructure via API", Terminal],
  ],
  Developers: [
    ["/developers", "Developer hub", "Everything for builders", Terminal],
    ["/docs/quickstart", "Quickstart", "Send in five minutes", FileText],
    ["/docs/api-reference", "API reference", "Endpoints and schemas", Code2],
    ["/docs/webhooks", "Webhooks", "Signed event delivery", Webhook],
    ["/changelog", "Changelog", "What shipped recently", History],
    ["/status", "Status", "Live system health", Activity],
  ],
  Resources: [
    ["/docs", "Documentation", "Guides and concepts", BookOpen],
    ["/integrations", "Integrations", "Connected and planned", Plug],
    ["/security", "Security", "How we protect data", Lock],
    ["/about", "About", "Why we build Nimbly", Building2],
    ["/contact", "Contact", "Talk to the team", Mail],
  ],
};

function Dropdown({ name, items }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();
  const loc = useLocation();
  useEffect(() => { setOpen(false); }, [loc.pathname]);
  useEffect(() => {
    const h = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  return (
    <div ref={ref} className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button className="btn-ghost h-9" aria-expanded={open} onClick={() => setOpen(!open)} data-testid={`nav-${name.toLowerCase()}-trigger`}>
        {name}<ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute left-0 top-full pt-2 z-50">
          <div className={`card shadow-[0_12px_40px_-12px_rgba(20,33,61,.25)] p-2 grid gap-0.5 ${items.length > 6 ? "grid-cols-2 w-[560px]" : "w-[300px]"}`} data-testid={`nav-${name.toLowerCase()}-menu`}>
            {items.map(([to, t, d, I]) => (
              <Link key={to} to={to} className="flex gap-3 rounded-md p-2.5 hover:bg-[#F6F8FC] group" data-testid={`nav-link-${to.replace(/\//g, "-").slice(1)}`}>
                <span className="h-8 w-8 shrink-0 rounded-md border border-[#E2E8F0] grid place-items-center text-[#245BDB] group-hover:border-[#C9D7F5] bg-white"><I size={15} /></span>
                <span><span className="block text-sm font-medium text-[#111827]">{t}</span><span className="block text-xs muted">{d}</span></span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function Nav() {
  const [mobile, setMobile] = useState(false);
  const loc = useLocation();
  useEffect(() => { setMobile(false); }, [loc.pathname]);
  const authed = !!localStorage.getItem("nimbly_token");
  return (
    <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-[#E2E8F0]">
      <div className="max-w-[1240px] mx-auto px-5 h-16 flex items-center gap-6">
        <Logo />
        <nav className="hidden lg:flex items-center gap-0.5">
          {Object.entries(MENUS).map(([n, items]) => <Dropdown key={n} name={n} items={items} />)}
          <NavLink to="/pricing" className="btn-ghost" data-testid="nav-pricing">Pricing</NavLink>
        </nav>
        <div className="ml-auto hidden lg:flex items-center gap-2">
          {authed ? <Link to="/app" className="btn-primary" data-testid="nav-dashboard">Open dashboard</Link> : <>
            <Link to="/login" className="btn-ghost" data-testid="nav-signin">Sign in</Link>
            <Link to="/signup" className="btn-primary" data-testid="nav-get-started">Get started</Link></>}
        </div>
        <button className="lg:hidden ml-auto btn-ghost" onClick={() => setMobile(!mobile)} aria-label="Menu" data-testid="nav-mobile-toggle">{mobile ? <X size={18} /> : <Menu size={18} />}</button>
      </div>
      {mobile && (
        <div className="lg:hidden border-t border-[#E2E8F0] bg-white max-h-[80vh] overflow-y-auto px-5 py-4 space-y-5" data-testid="nav-mobile-menu">
          {Object.entries(MENUS).map(([n, items]) => (
            <div key={n}><div className="eyebrow mb-2">{n}</div>
              <div className="grid grid-cols-2 gap-1">{items.map(([to, t]) => <Link key={to} to={to} className="text-sm py-1.5 text-[#111827]">{t}</Link>)}</div></div>
          ))}
          <div className="flex gap-2 pt-2"><Link to="/pricing" className="btn-secondary flex-1">Pricing</Link><Link to="/login" className="btn-secondary flex-1">Sign in</Link><Link to="/signup" className="btn-primary flex-1">Get started</Link></div>
        </div>
      )}
    </header>
  );
}

const FOOT = {
  Product: [["/product/ai-agents", "AI agents"], ["/product/shared-inbox", "Shared inbox"], ["/product/email-api", "Email API"], ["/product/inbound-email", "Inbound email"], ["/product/knowledge-base", "Knowledge base"], ["/product/automations", "Automations"], ["/product/analytics", "Analytics"], ["/pricing", "Pricing"]],
  Solutions: [["/solutions/ecommerce", "E-commerce"], ["/solutions/saas", "SaaS"], ["/solutions/small-business", "Small business"], ["/solutions/service-businesses", "Service businesses"], ["/solutions/developers", "Developers"]],
  Developers: [["/docs", "Documentation"], ["/docs/quickstart", "Quickstart"], ["/docs/api-reference", "API reference"], ["/docs/webhooks", "Webhooks"], ["/changelog", "Changelog"], ["/status", "Status"]],
  Company: [["/about", "About"], ["/security", "Security"], ["/integrations", "Integrations"], ["/contact", "Contact"], ["/privacy", "Privacy policy"], ["/terms", "Terms of service"]],
};

export function Footer() {
  return (
    <footer className="bg-[#14213D] text-[#AFC0E0]">
      <div className="max-w-[1240px] mx-auto px-5 py-16 grid grid-cols-2 md:grid-cols-6 gap-10">
        <div className="col-span-2">
          <Logo dark />
          <p className="text-sm mt-4 max-w-xs leading-relaxed">Customer support, on autopilot. AI agents, email infrastructure and automation in one platform.</p>
          <p className="text-xs mt-6 text-[#7F92B8]">Nimbly is an early-stage product. Features marked "planned" are on the roadmap and not yet available.</p>
        </div>
        {Object.entries(FOOT).map(([h, ls]) => (
          <div key={h}><div className="text-xs font-semibold text-white uppercase tracking-wider mb-3">{h}</div>
            <ul className="space-y-2">{ls.map(([to, t]) => <li key={to}><Link to={to} className="text-sm hover:text-white transition-colors">{t}</Link></li>)}</ul></div>
        ))}
      </div>
      <div className="border-t border-[#22335A]"><div className="max-w-[1240px] mx-auto px-5 h-14 flex items-center justify-between text-xs text-[#7F92B8]"><span>© {new Date().getFullYear()} Nimbly</span><Link to="/status" className="flex items-center gap-2 hover:text-white"><span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />System status</Link></div></div>
    </footer>
  );
}

export function MarketingLayout({ children }) {
  const loc = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [loc.pathname]);
  return <div className="min-h-screen flex flex-col bg-white"><Nav /><main className="flex-1">{children}</main><Footer /></div>;
}
